import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CrashScreen, ErrorBoundary } from '@munifocus/ui';

/**
 * Wraps the app in a localized, on-brand crash page. A hard reload (rather than
 * an in-place reset) is deliberate: once a render has thrown, the surrounding
 * state is suspect, so we rebuild from a clean boot.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      onError={(error, info) => {
        console.error('Government Portal crashed:', error, info.componentStack);
      }}
      fallback={({ error }) => (
        <CrashScreen
          title={t('common.crash.title')}
          description={t('common.crash.description')}
          reloadLabel={t('common.crash.reload')}
          onReload={() => window.location.reload()}
          homeLabel={t('common.crash.home')}
          onHome={() => window.location.assign('/')}
          detailLabel={t('common.crash.detailLabel')}
          detail={import.meta.env.DEV ? (error.stack ?? error.message) : undefined}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
